import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, Users } from "lucide-react";
import { pick, useLang } from "@/i18n";
import { mediaUrl, publicMedia } from "@/api";
import { GoldImage } from "./GoldImage";

export const PackageCard = ({ pkg, index = 0 }) => {
    const { lang, t } = useLang();
    const img = pkg.cover_media_id ? mediaUrl(pkg.cover_media_id) : publicMedia(pkg.cover_image_url, true);
    const durationLabel = pick(pkg.duration_label, lang) || (pkg.duration_hours != null ? `${pkg.duration_hours} ${pkg.duration_hours === 1 ? t.pkg.hour : t.pkg.hours}` : null);
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: (index % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }}
        >
            <Link
                to={`/pacchetto/${pkg.id}`}
                data-testid={`package-card-${pkg.code || pkg.id}`}
                className="group gold-card block rounded-2xl overflow-hidden"
            >
                <div className="relative aspect-[4/3] overflow-hidden">
                    <GoldImage
                        src={img}
                        alt={pick(pkg.name, lang)}
                        label={pkg.code || pkg.category}
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#080909]/70 via-transparent to-transparent pointer-events-none" />
                </div>
                <div className="p-6">
                    <h3 className="font-display text-xl sm:text-2xl text-amber-50 leading-snug">{pick(pkg.name, lang)}</h3>
                    <p className="mt-2 text-sm text-stone-400 leading-relaxed line-clamp-2">{pick(pkg.short_description, lang)}</p>
                    <div className="mt-4 space-y-1.5 text-xs text-stone-500">
                        {durationLabel && (
                            <span className="flex items-center gap-2"><Clock size={13} className="text-amber-500/80" /> {durationLabel}</span>
                        )}
                        {pkg.max_passengers != null && (
                            <span className="flex items-center gap-2"><Users size={13} className="text-amber-500/80" /> {t.pkg.maxPassengers}: {pkg.max_passengers}</span>
                        )}
                    </div>
                    <div className="mt-5 flex items-center justify-between border-t border-amber-500/15 pt-4">
                        {pkg.price != null ? (
                            <span data-testid={`package-price-${pkg.code || pkg.id}`} className="font-display text-xl text-amber-300">
                                {t.common.from} €{pkg.price.toFixed(2)}
                            </span>
                        ) : <span />}
                        <span className="flex h-10 w-10 items-center justify-center rounded-full border border-amber-500/45 text-amber-400 transition-all duration-300 group-hover:bg-amber-400 group-hover:text-black" aria-label={t.home?.discover || t.common.learnMore}>
                            <ArrowRight size={16} />
                        </span>
                    </div>
                </div>
            </Link>
        </motion.div>
    );
};
